import { createSlice, current } from "@reduxjs/toolkit";

export const CART_STORAGE_KEY = "cart-state";
export const DEFAULT_CART_DATA = {
  type: "",
  itemCount: 0,
  items: [],
  productId: null,
};

const getHydratedCartData = () => {
  if (typeof window === "undefined") {
    return DEFAULT_CART_DATA;
  }

  try {
    const storedCart = localStorage.getItem(CART_STORAGE_KEY);
    if (!storedCart) {
      return DEFAULT_CART_DATA;
    }

    const parsed = JSON.parse(storedCart);
    const safeItems = Array.isArray(parsed?.items) ? parsed.items : [];
    const itemCount = safeItems.reduce((total, item) => {
      const qty = Number(item?.selectedQuantity) || 0;
      return total + qty;
    }, 0);

    return {
      ...DEFAULT_CART_DATA,
      ...parsed,
      items: safeItems,
      itemCount,
    };
  } catch (error) {
    console.warn(
      "Unable to parse stored cart data. Falling back to defaults.",
      error,
    );
    return DEFAULT_CART_DATA;
  }
};

const getSafeCartItems = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items;
};

// #TODO: Save to localstorage
const cartSlice = createSlice({
  name: "cart",
  initialState: {
    cartData: getHydratedCartData(),
  },
  reducers: {
    updateNumberCartItems: (state, action) => {
      const cartItems = getSafeCartItems(state.cartData.items);
      const currentItemState = current(cartItems);
      const currentItemIndex = currentItemState.findIndex((x) => {
        return x?.productId === action.payload.productId;
      });
      const existingCartItem = currentItemState?.[currentItemIndex];

      if (!Array.isArray(state.cartData.items)) {
        state.cartData.items = [];
      }

      if (action.payload.type === "add") {
        if (existingCartItem) {
          const updatedItem = {
            ...existingCartItem,
            type: "add",
            selectedQuantity:
              +existingCartItem.selectedQuantity +
              action.payload.selectedQuantity,
          };
          state.cartData.items = [...state.cartData.items];
          state.cartData.items[currentItemIndex] = updatedItem;
          state.cartData.itemCount =
            +state.cartData.itemCount + action.payload.selectedQuantity;
        } else {
          state.cartData.items.push(action.payload);
          state.cartData.itemCount += +action.payload.selectedQuantity;
        }
      } else if (action.payload.type === "subtract") {
        if (existingCartItem) {
          const updatedItem = {
            ...existingCartItem,
            type: "subtract",
            selectedQuantity: +existingCartItem.selectedQuantity - 1,
          };
          state.cartData.items = [...state.cartData.items];
          if (updatedItem.selectedQuantity <= 0) {
            state.cartData.items.splice(currentItemIndex, 1);
          } else {
            state.cartData.items[currentItemIndex] = updatedItem;
          }
          state.cartData.itemCount = +state.cartData.itemCount - 1;
        } else {
          state.cartData.items.push(action.payload);
          state.cartData.itemCount -= +action.payload.selectedQuantity;
        }
      }
    },

    deleteFromCart: (state, action) => {
      const cartItems = getSafeCartItems(state.cartData.items);
      const currentItemState = current(cartItems);
      const currentItemIndex = currentItemState.findIndex((x) => {
        return x?.productId === action.payload.productId;
      });
      const existingCartItem = currentItemState?.[currentItemIndex];
      const existingCartItemQty = existingCartItem?.selectedQuantity || 0;

      if (currentItemIndex === -1) {
        return;
      }

      state.cartData.items = currentItemState.filter(
        (_, index) => index !== currentItemIndex,
      );
      state.cartData.itemCount = Math.max(
        0,
        +state.cartData.itemCount - existingCartItemQty,
      );
    },

    removeAllCartItems: (state, action) => {
      const safePayload = {
        ...DEFAULT_CART_DATA,
        ...(action.payload || {}),
        items: getSafeCartItems(action.payload?.items),
      };
      state.cartData = safePayload;
    },
  },
});

export const { deleteFromCart, updateNumberCartItems, removeAllCartItems } =
  cartSlice.actions;
export default cartSlice;
